import React, { createContext, useContext } from 'react';
import type { DeepcThemeProviderProps } from '@deepc/ui';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import { AlertDisplay, OAuthRequestDialog } from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FeatureFlagged, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { ProxiedSignInPage } from '@backstage/core-components';

import { deepcTheme, deepcLightTheme } from './Theme';
import { UnifiedThemeProvider } from '@backstage/theme';
import DarkIcon from '@mui/icons-material/Brightness2';
import LightIcon from '@mui/icons-material/WbSunny';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { FleetPage } from '@internal/backstage-plugin-fleet';
import { OverviewPage } from '@internal/backstage-plugin-overview';
import { OrganisationPage } from '@internal/backstage-plugin-organisation';
import { SecurityPage } from '@internal/backstage-plugin-security';
import {
  ReportingPage,
  AnalyticsDetailPage,
  EngineComparisonPage,
} from '@internal/backstage-plugin-reporting';
import { SolutionsPage } from '@internal/backstage-plugin-solutions-frontend';
import { SupportPage } from '@internal/backstage-plugin-support-frontend';
import { EnginesListPage } from '@deepc/backstage-plugin-engines-frontend';
import { RadpairPage } from '@internal/backstage-plugin-radpair';
import { FindingExplorerPage } from '@internal/backstage-plugin-finding-explorer';
import {
  PortfolioPage,
  PortfolioEngineDetailsPage,
  PortfolioAdminPage,
} from '@internal/backstage-plugin-portfolio';
import { SubmissionFlowPage } from '@internal/backstage-plugin-submission-flow';
import {
  BillingPage,
  BILLING_FEATURE_FLAG,
} from '@internal/backstage-plugin-billing';
import {
  DiscoverPage,
  DISCOVER_FEATURE_FLAG,
} from '@internal/backstage-plugin-discover';
import { NotFoundPage } from './NotFoundPage';
import { DocsV2Page } from './components/DocsV2Page';

// Default to dark theme on first visit — Backstage falls back to light when
// multiple themes are registered and no OS preference is detected.
(() => {
  try {
    if (!localStorage.getItem('theme')) {
      localStorage.setItem('theme', 'dark');
    }
  } catch {
    // ignore
  }
})();

const ComparisonPaletteContext =
  createContext<DeepcThemeProviderProps['primary']>(undefined);

const comparisonPrimary = (theme: typeof deepcTheme) => {
  const palette = theme.getTheme('v5')?.palette;
  return (
    palette && {
      ...palette.primary,
      contrastText: palette.getContrastText(palette.primary.main),
    }
  );
};

const ThemedEngineComparisonPage = () => (
  <EngineComparisonPage primary={useContext(ComparisonPaletteContext)} />
);

const app = createApp({
  featureFlags: [
    {
      pluginId: '',
      name: 'deepc-one',
      description: 'Enable full functionality.',
    },
    {
      pluginId: '',
      name: 'show-system-manager-gate-config',
      description:
        'Gate configuration in the system manager: view it, and vendors add and remove their AI solutions',
    },
  ],
  themes: [
    {
      id: 'dark',
      title: 'Dark Theme',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <ComparisonPaletteContext.Provider
          value={comparisonPrimary(deepcTheme)}
        >
          <UnifiedThemeProvider children={children} theme={deepcTheme} />
        </ComparisonPaletteContext.Provider>
      ),
    },
    {
      id: 'light',
      title: 'Light Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <ComparisonPaletteContext.Provider
          value={comparisonPrimary(deepcLightTheme)}
        >
          <UnifiedThemeProvider children={children} theme={deepcLightTheme} />
        </ComparisonPaletteContext.Provider>
      ),
    },
  ],
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      if (configApi.getString('auth.environment') === 'development') {
        const developmentUserEmail =
          configApi.getOptionalString('auth.developmentUserEmail') ||
          'paul@deepc.ai';
        return (
          <ProxiedSignInPage
            {...props}
            provider="oauth2Proxy"
            headers={async () => {
              return {
                'x-forwarded-user': developmentUserEmail,
                'x-forwarded-email': developmentUserEmail,
              };
            }}
          />
        );
      }
      return <ProxiedSignInPage {...props} provider="oauth2Proxy" />;
    },
  },
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="analytics" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/fleet" element={<FleetPage />} />
    <Route path="/overview" element={<OverviewPage />} />
    <Route path="/organisation" element={<OrganisationPage />} />
    <Route path="/security" element={<SecurityPage />} />
    <Route path="/analytics" element={<ReportingPage />} />
    <Route path="/analytics/compare" element={<ThemedEngineComparisonPage />} />
    <Route path="/analytics/:udi" element={<AnalyticsDetailPage />} />
    <Route path="/solutions" element={<SolutionsPage />} />
    <Route path="/engines/list" element={<EnginesListPage />} />
    <Route path="/support" element={<SupportPage />} />
    <Route path="/reporting" element={<RadpairPage />} />
    <Route path="/finding-explorer" element={<FindingExplorerPage />} />
    <Route
      path="/portfolio"
      element={<PortfolioPage basePath="/portfolio" />}
    />
    <Route
      path="/portfolio/admin"
      element={<PortfolioAdminPage basePath="/portfolio" />}
    />
    <Route
      path="/portfolio/:slug"
      element={<PortfolioEngineDetailsPage basePath="/portfolio" />}
    />
    <Route path="/submit-ai" element={<SubmissionFlowPage />} />
    <FeatureFlagged with={DISCOVER_FEATURE_FLAG}>
      <Route path="/discover" element={<DiscoverPage />} />
    </FeatureFlagged>
    <FeatureFlagged with={BILLING_FEATURE_FLAG}>
      <Route path="/billing" element={<BillingPage />} />
    </FeatureFlagged>
    <Route path="/docs-v2/*" element={<DocsV2Page />} />
    <Route path="/*" element={<NotFoundPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
